<template>
    <div>
        <div v-if="shouldShowAdminResponseTime" class="mt-4 text-right text-smaller">
            <template v-if="hasResponseTime">
                <CText>Ako rýchlo chcem reagovať: </CText>
                do
                <NuxtLink
                    class="link underline"
                    :event="preview ? '' : 'click'"
                    :to="{ name: 'profile-settings', hash: '#response-time' }"
                    >{{ parsedResponseTime }}</NuxtLink
                >
                <CTooltip>
                    Tvoj odhadovaný čas, za ktorý vieš odpovedať. Zobrazí sa v tvojom dobrovoľníckom a mentoringovom
                    profile s cieľom nastaviť reálne očakávania návštevníkov GROWNi (zvyšujeme tým ich spokojnosť).
                    Porovnaj si ho so skutočným časom, ktorý ti automaticky počíta systém.
                </CTooltip>
            </template>
            <template v-else>
                <div class="text-smaller">
                    <CText>Ako rýchlo chcem reagovať:</CText>
                    <NuxtLink
                        class="link underline"
                        :class="{ 'cursor-default': preview }"
                        :event="preview ? '' : 'click'"
                        :to="{ name: 'profile-settings', hash: '#response-time' }"
                        >zadať údaj</NuxtLink
                    >
                    <CTooltip>
                        Tvoj odhadovaný čas, za ktorý vieš odpovedať. Zobrazí sa v tvojom dobrovoľníckom a mentoringovom
                        profile s cieľom nastaviť reálne očakávania návštevníkov GROWNi (zvyšujeme tým ich spokojnosť).
                        Porovnaj si ho so skutočným časom, ktorý ti automaticky počíta systém.
                    </CTooltip>
                </div>
            </template>
            <br />
            <template v-if="hasCardResponseTime">
                Ako rýchlo reagujem:
                <CText variant="muted"> do {{ parsedCardResponseTime }}</CText>
                <CTooltip
                    >Čas vidíš iba ty. Počíta sa podľa rýchlosti tvojich odpovedí v Komunikácii týkajúcej sa
                    <b>{{ parsedTitleInTooltip }}</b> a prestane sa počítať keď klikneš na
                    Spustenie/Odmietnutie.</CTooltip
                >
            </template>
            <template v-else>
                <div class="text-smaller">
                    Ako rýchlo reagujem:
                    <CText variant="muted">čaká sa na dáta</CText>
                    <CTooltip>
                        Čas vidíš iba ty. Počíta sa podľa rýchlosti tvojich odpovedí v Komunikácii týkajúcej sa
                        <b>{{ parsedTitleInTooltip }}</b> a prestane sa počítať keď klikneš na Spustenie/Odmietnutie.
                    </CTooltip>
                </div>
            </template>
        </div>

        <template v-else-if="hasResponseTime">
            <CCardBox>
                <CText class="font-medium">Ako rýchlo reagujem:</CText>
                <p>do {{ parsedResponseTime }}</p>
            </CCardBox>
        </template>

        <template v-else>
            <CCardBox v-if="isMineMentoringProfile">
                <CText>Ako rýchlo reagujem:</CText>
                <NuxtLink
                    class="link underline"
                    :class="{ 'cursor-default': preview }"
                    :event="preview ? '' : 'click'"
                    :to="{ name: 'profile-settings', hash: '#response-time' }"
                    >zadať údaj</NuxtLink
                >
            </CCardBox>
        </template>
    </div>
</template>

<script setup lang="ts">
import type { Mentoring } from '#shared/types/Mentoring'
import type { ParticipationRequest } from '#shared/types/ParticipationRequest'
import type { UserProfile } from '#shared/types/User'
import { getFullName, parseManuallyAddedResponseTime, parseResponseTime } from '#shared/utils'
import { useMentoring } from '@/composables/mentoring/useMentoring'
import { useProjects } from '@/composables/projects/useProjects'

const { adjustTitleByStatusForMentor } = useMentoring()
const { adjustAskForVolunteerTitleByStatusForVolunteer } = useProjects()

const props = defineProps<{
    user: UserProfile
    cardData?: Mentoring | ParticipationRequest
    shouldShowAdminResponseTime?: boolean
    preview?: boolean
}>()

const store = useApplicationStore()
const userId = computed(() => store.userId)

const hasResponseTime = computed((): boolean => {
    return Boolean(props.user.responseTime) && Boolean(props.user.responseUnit)
})

const hasCardResponseTime = computed((): boolean => {
    return Boolean(props.cardData) && Boolean(props.cardData.responsesAverageTime)
})

const parsedResponseTime = computed((): string | undefined => {
    return parseManuallyAddedResponseTime(props.user.responseTime, props.user.responseUnit)
})

const parsedCardResponseTime = computed((): string | null => {
    return parseResponseTime(props.cardData?.responsesAverageTime)
})

const isMineMentoringProfile = computed((): boolean => {
    return userId.value === props.user.id
})

const parsedTitleInTooltip = computed((): string => {
    if (isMentoring(props.cardData)) {
        const mentoring = props.cardData as Mentoring

        const { before, title, after } = adjustTitleByStatusForMentor(mentoring, getFullName(mentoring.menteeMeta))

        return `${before} ${title} ${after}`
    } else {
        const participationRequest = props.cardData as ParticipationRequest

        const { before, title, after } = adjustAskForVolunteerTitleByStatusForVolunteer(
            participationRequest,
            participationRequest.orgMeta!.title!
        )

        return `${before} ${title} ${after}`
    }
})

function isMentoring(variableToCheck: any): boolean {
    return variableToCheck?.mentorId !== undefined
}
</script>
